import React, { useEffect, useRef } from 'react';
import { Check, Pipette } from 'lucide-react';
import { COVER_PRESETS, coverStyle } from '../lib/covers';

interface CoverPickerProps {
  /** Current cover "#rrggbb". */
  value: string;
  /** Live preview while choosing (every wheel movement). */
  onPreview: (hex: string) => void;
  /** Commit the choice — called once per pick, debounced for the wheel. */
  onCommit: (hex: string) => void;
}

const COMMIT_DELAY_MS = 500;

/**
 * Preset cover swatches plus a "custom" swatch that opens the browser's native
 * color wheel. The wheel previews live and commits after the user stops.
 */
export const CoverPicker: React.FC<CoverPickerProps> = ({ value, onPreview, onCommit }) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const current = value.toLowerCase();
  const isCustom = !COVER_PRESETS.some((p) => p.hex === current);

  const pickPreset = (hex: string) => {
    if (timer.current) clearTimeout(timer.current);
    onPreview(hex);
    onCommit(hex);
  };

  const pickCustom = (hex: string) => {
    onPreview(hex);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(hex), COMMIT_DELAY_MS);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Cover color">
      {COVER_PRESETS.map((p) => {
        const selected = p.hex === current;
        return (
          <button
            key={p.hex}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={p.name}
            title={p.name}
            onClick={() => pickPreset(p.hex)}
            className={[
              'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border transition-transform hover:scale-110',
              selected ? 'border-[3px] border-white ring-2 ring-black/30' : 'border-black/10',
            ].join(' ')}
            style={{ background: p.hex }}
          >
            {selected && <Check size={14} color={coverStyle(p.hex).text} />}
          </button>
        );
      })}

      <label
        title="Custom color"
        className={[
          'relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border transition-transform hover:scale-110',
          isCustom ? 'border-[3px] border-white ring-2 ring-black/30' : 'border-black/10',
        ].join(' ')}
        style={{
          background: isCustom
            ? current
            : 'conic-gradient(#ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)',
        }}
      >
        <Pipette size={13} color={isCustom ? coverStyle(current).text : '#ffffff'} />
        <input
          type="color"
          aria-label="Custom color"
          value={current}
          onChange={(e) => pickCustom(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </label>
    </div>
  );
};
